import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { useUserLocations } from '@/features/voice-campaigns/api';
import { useEditCampaignLocation } from '../api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: number;
  campaignName: string;
  /** The location IDs currently bound (from the campaign row). */
  currentLocationIds?: string[];
  onSaved?: () => void;
}

export function EditCampaignLocationDialog({
  open,
  onOpenChange,
  campaignId,
  campaignName,
  currentLocationIds = [],
  onSaved,
}: Props) {
  const locations = useUserLocations();
  const edit = useEditCampaignLocation();

  const [selected, setSelected] = useState<string[]>(currentLocationIds);

  // Re-sync the controlled selection whenever the dialog re-opens for a
  // different campaign.
  useEffect(() => {
    if (open) setSelected(currentLocationIds);
  }, [open, currentLocationIds]);

  const options = useMemo(
    () =>
      (locations.data ?? []).map((l) => ({
        value: String(l.locationId),
        label: l.locationName,
      })),
    [locations.data],
  );

  const handleSave = () => {
    if (selected.length === 0) {
      toast.error('Pick at least one location.');
      return;
    }
    edit.mutate(
      { campaignId, locationIds: selected },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Location list updated');
          onSaved?.();
          onOpenChange(false);
        },
        onError: () => toast.error('Could not update the campaign locations.'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit locations</DialogTitle>
          <DialogDescription>
            Update which locations{' '}
            <span className="font-medium text-foreground">{campaignName}</span>{' '}
            dials from.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>
            Locations
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <MultiSelect
            options={options}
            value={selected}
            onChange={setSelected}
            placeholder={
              locations.isLoading
                ? 'Loading…'
                : options.length === 0
                  ? 'No locations available'
                  : 'Pick locations'
            }
            searchPlaceholder="Filter locations…"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={edit.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={edit.isPending}>
            {edit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
